import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';

import { CharacterBubbleAnimation } from '../../../../constants/character-bubble-animations.ts';
import { StepGraderType } from '../../../../constants/grading.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../../../constants/speech-recognition.ts';
import { selectCurrentLessonTaskStepKey } from '../../../../selectors/current-lesson-task-step-key.selector.ts';
import { triggerCharacterBubbleAnimationThunk } from '../../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible, resetBallPosition, positionBallAtWord } from '../../../../slices/ball/ball.slice.ts';
import { KeyWordType } from '../../../../slices/story-reading/story-reading.slice.types.ts';
import { computeStepWordIndicesThunk } from '../../../../slices/story-reading/thunks/compute-step-word-indices.thunk.ts';
import { BasePronunciationGraderInterface } from '../../../../types/pronunciation-grader.types.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

export class StoryUserSayWordStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: StoryUserSayWordStateChangeHandlerUtil;
  private grader: BasePronunciationGraderInterface | null = null;

  static get instance(): StoryUserSayWordStateChangeHandlerUtil {
    if (!StoryUserSayWordStateChangeHandlerUtil._instance) {
      StoryUserSayWordStateChangeHandlerUtil._instance = new StoryUserSayWordStateChangeHandlerUtil();
    }
    return StoryUserSayWordStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'StoryUserSayWord';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    await this.handleStartStep();
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onRecording(): Promise<void> {
    console.log(`${this.getHandlerName()} onRecording`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(true));

    try {
      if (!this.grader) {
        throw new Error('Grader not initialized');
      }

      const result = await this.grader.startGrading();
      await dispatch(updateAppStateThunk({ state: result.isCorrect ? AppStateEnum.Correct : AppStateEnum.Incorrect }));
    } catch (error) {
      this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onPlaying(): Promise<void> {
    await this.handleInvalidState();
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await this.handleCorrect({
      answer: SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
      assuming: false,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await this.handleCorrect({
      answer: SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
      assuming: true,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await this.handleIncorrect(SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const state = await this.reduxStoreUtil.getRootState();
    const stepKey = selectCurrentLessonTaskStepKey(state);
    if (this.lessonsUtil.isFinalStep(stepKey)) {
      await dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));

      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();

      dispatch(setBallIsVisible(true));
    } else {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep }));
    }
  }

  protected override async onAborting(): Promise<void> {
    await super.onAborting();
    console.log(`${this.getHandlerName()} onAborting`);
    await this.grader?.abort();
  }

  // Helper methods

  private async handleStartStep(): Promise<void> {
    try {
      const dispatch = await this.reduxStoreUtil.getDispatch();

      // Reset ball position
      dispatch(resetBallPosition());

      // Compute word indices and position ball
      await dispatch(computeStepWordIndicesThunk()).unwrap();

      const state = await this.reduxStoreUtil.getRootState();
      const wordPositions = state.storyReading.keyWordPositions;
      const firstWordPosition = wordPositions[KeyWordType.STEP_FIRST_WORD];
      if (firstWordPosition) {
        dispatch(
          positionBallAtWord({
            xPosition: firstWordPosition.startX,
            rowIndex: firstWordPosition.line,
            isVisible: false, // Keep it hidden until we're ready
          })
        );
      }

      // Initialize grader
      try {
        this.grader = await this.pronunciationGraderFactory.createGrader({
          type: StepGraderType.STORY_WORD,
        });
      } catch (error) {
        this.handleError(error as Error);
      }

      // Speak step
      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();

      // Show ball when ready
      dispatch(setBallIsVisible(true));
    } catch (error) {
      console.error('Error in handleStartStep', error);
      this.handleError(error as Error);
    }
  }
}
export const getStoryUserSayWordStateChangeHandlerUtil = (): StoryUserSayWordStateChangeHandlerUtil =>
  StoryUserSayWordStateChangeHandlerUtil.instance;
